class LocalizationDrawer extends HTMLElement {
  constructor() {
    super();

    this.drawerClass = "wt-localization-drawer";
    this.drawer = this;
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "wt-localization-drawer-overlay";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;

    this.triggerQuery = [
      ".wt-localization-trigger--local",
      ".wt-localization-drawer__close",
      `.${this.pageOverlayClass}`,
    ].join(", ");

    this.isOpen = false;
    this.lastTrigger = null;
  }

  connectedCallback() {
    this.init();
  }

  getFocusableElements() {
    const focusableSelector =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusableElements = Array.from(
      this.querySelectorAll(focusableSelector),
    ).filter((el) => !el.hasAttribute("disabled") && el.tabIndex >= 0);

    return {
      first: focusableElements[0],
      last: focusableElements[focusableElements.length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
    setTimeout(() => {
      document.body.classList.remove("no-focus-visible");
    }, 200);
  }

  updateTriggerExpanded(isExpanded) {
    document
      .querySelectorAll(".wt-localization-trigger--local")
      .forEach((trigger) => {
        trigger.setAttribute("aria-expanded", isExpanded ? "true" : "false");
      });
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.hidden = true;
      this.isOpen = false;
      this.updateTriggerExpanded(false);
      if (this.lastTrigger) this.lastTrigger.focus();
    } else {
      this.setAttribute("open", "");
      this.hidden = false;
      this.isOpen = true;
      this.updateTriggerExpanded(true);
      const closeBtn = this.querySelector(".wt-localization-drawer__close");
      if (closeBtn) closeBtn.focus();
    }
    this.temporaryHideFocusVisible();
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);
  }

  setActiveTab(tabKey) {
    const tabTriggers = this.querySelectorAll(
      ".wt-localization-drawer__tab__trigger",
    );
    const tabContents = this.querySelectorAll(
      ".wt-localization-drawer__tab__content",
    );

    tabTriggers.forEach((trigger) => {
      const isActive = trigger.dataset.tabTarget === tabKey;
      trigger.classList.toggle(
        "wt-localization-drawer__tab__trigger--active",
        isActive,
      );
      trigger.setAttribute("aria-selected", isActive ? "true" : "false");
    });

    tabContents.forEach((panel) => {
      const isActive = panel.dataset.tabContent === tabKey;
      panel.classList.toggle("is-active", isActive);
      panel.setAttribute("aria-hidden", isActive ? "false" : "true");
    });
  }

  init() {
    this.updateTriggerExpanded(false);

    this.addEventListener("keydown", (e) => {
      if (!this.isOpen) return;
      const { first, last } = this.getFocusableElements();
      const isTab = e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const isEsc =
        e.key === "Escape" || e.keyCode === 27 || e.code === "Escape";

      if (isEsc) {
        e.preventDefault();
        this.toggleDrawerClasses();
      } else if (isTab) {
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });

    document.querySelectorAll(this.triggerQuery).forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();

        const openTab = trigger.dataset.openDrawer;

        if (this.isOpen && openTab) {
          this.setActiveTab(openTab);
          this.lastTrigger = trigger;
          return;
        }

        this.toggleDrawerClasses();

        if (openTab) {
          this.setActiveTab(openTab);
        }

        if (trigger.classList.contains("wt-localization-trigger--local")) {
          this.lastTrigger = trigger;
        }
      });
    });

    const searchInput = this.querySelector(
      ".wt-localization-drawer__search-country__input",
    );
    if (searchInput) {
      searchInput.addEventListener("input", () => {
        const searchValue = searchInput.value.toLowerCase().trim();

        this.querySelectorAll(".country-selector__item").forEach((item) => {
          const countryName = (item.dataset.filterName || "").toLowerCase();
          item.style.display = countryName.includes(searchValue) ? "" : "none";
        });
      });
    }

    const tabTriggers = this.querySelectorAll(
      ".wt-localization-drawer__tab__trigger",
    );

    tabTriggers.forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        const targetKey = trigger.dataset.tabTarget;
        if (targetKey) this.setActiveTab(targetKey);
      });
    });

    const countryForm = document.querySelector("#DrawerCountryForm");
    if (countryForm) {
      const countryInput = countryForm.querySelector('[name="country_code"]');
      const countryLinks = this.querySelectorAll(".country-selector__trigger");

      countryLinks.forEach((link) => {
        link.addEventListener("click", (e) => {
          e.preventDefault();

          countryInput.value = link.dataset.value;
          countryForm.submit();
        });
      });
    }

    const languageForm = this.querySelector("form#DrawerLanguageForm");
    if (languageForm) {
      const localeInput = languageForm.querySelector('[name="locale_code"]');
      const languageButtons = languageForm.querySelectorAll(
        ".wt-language-list__label[data-value]",
      );

      languageButtons.forEach((button) => {
        button.addEventListener("click", (e) => {
          e.preventDefault();

          const value = button.dataset.value;
          if (!value || !localeInput || localeInput.value === value) return;

          localeInput.value = value;
          languageButtons.forEach((btn) => {
            btn.disabled = btn !== button;
            btn.setAttribute("aria-busy", btn === button ? "true" : "false");
          });
          button.classList.add("wt-language-list__label--loading");
          languageForm.submit();
        });
      });
    }
  }
}

customElements.define("localization-drawer", LocalizationDrawer);
